import { useCallback, useEffect, useMemo, useState } from 'react'
import { useV2Api } from '../../../hooks/useV2Api'
import { friendlyErrorMessage } from '../../../utils/friendlyError'
import './V2Providers.css'

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'ocean_freight', label: 'International Ocean Freight', filterField: 'service_type' },
  { key: 'domestic_trucking', label: 'Domestic Trucking', filterField: 'service_type' },
]

const PROVIDER_TYPE_OPTIONS = [
  { value: 'ocean_freight', label: 'International Ocean Freight' },
  { value: 'domestic_trucking', label: 'Domestic Trucking' },
]

function reliabilityPct(score) {
  const n = typeof score === 'string' ? parseFloat(score) : score
  return Math.max(0, Math.min(100, Math.round((n || 0) * 100)))
}

function reliabilityColor(pct) {
  if (pct >= 85) return '#5C7A54'
  if (pct >= 70) return '#A67B2C'
  return '#8B3A3A'
}

// Which truckload modes a carrier handles. Decides whether an FTL or an LTL
// quote request reaches them at all, so trucking carriers must pick one.
const TRUCKING_MODE_OPTIONS = [
  { value: 'FTL', label: 'FTL only (full truckload)' },
  { value: 'LTL', label: 'LTL only (less-than-truckload)' },
  { value: 'both', label: 'Both FTL and LTL' },
]

const TRUCKING_MODE_LABELS = {
  FTL: 'FTL only',
  LTL: 'LTL only',
  both: 'FTL + LTL',
}

const isTrucking = (serviceType) =>
  serviceType === 'domestic_trucking' || serviceType === 'both'

const EMPTY_FORM = {
  name: '', contactEmail: '', contactName: '',
  serviceType: '', truckingMode: '', preferredChannel: 'email'
}

export default function V2ProviderList() {
  const api = useV2Api()
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorFallback, setErrorFallback] = useState(null)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [banner, setBanner] = useState(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null); setErrorFallback(null)
    try {
      const res = await api.request('/api/v2/providers')
      setProviders(res?.data || [])
    } catch (err) {
      setError(err); setErrorFallback('Failed to load providers')
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => {
    return providers.filter((p) => {
      const matchesFilter = filter === 'all' || (p.service_type || 'ocean_freight') === filter
      const matchesSearch = !search ||
        (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.contact_email || '').toLowerCase().includes(search.toLowerCase())
      return matchesFilter && matchesSearch
    })
  }, [providers, filter, search])

  const resetForm = () => {
    setForm(EMPTY_FORM)
    setEditingId(null)
    setShowForm(false)
  }

  const handleEdit = (provider) => {
    setEditingId(provider.id)
    setForm({
      name: provider.name || '',
      contactEmail: provider.contact_email || '',
      contactName: provider.contact_name || '',
      serviceType: provider.service_type || 'ocean_freight',
      truckingMode: provider.trucking_mode || '',
      preferredChannel: 'email'
    })
    setShowForm(true)
    setError(null)
    setErrorFallback(null)
  }

  const handleSave = async () => {
    if (!form.name || !form.serviceType || !form.contactEmail) {
      setError('Name, type, and contact email are required.'); setErrorFallback(null)
      return
    }
    if (isTrucking(form.serviceType) && !form.truckingMode) {
      setError('Pick whether this carrier handles FTL, LTL, or both — it decides which quote requests they receive.')
      setErrorFallback(null)
      return
    }
    setSaving(true); setError(null); setErrorFallback(null)
    try {
      const backendType = form.serviceType === 'domestic_trucking' ? 'CARRIER' : 'FORWARDER'
      // An ocean provider has no truckload mode, and an empty string is not a
      // legal value for the column — send null so it clears instead of failing.
      const payload = {
        ...form,
        type: backendType,
        truckingMode: isTrucking(form.serviceType) ? form.truckingMode : null
      }
      const isEditing = editingId !== null
      if (isEditing) {
        await api.request(`/api/v2/providers/${editingId}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        })
        setBanner({ kind: 'ok', text: `Provider "${form.name}" updated.` })
      } else {
        await api.request('/api/v2/providers', {
          method: 'POST',
          body: JSON.stringify(payload)
        })
        setBanner({ kind: 'ok', text: `Provider "${form.name}" added.` })
      }
      resetForm()
      await load()
    } catch (err) {
      setError(err)
      setErrorFallback(editingId !== null ? 'Failed to update provider' : 'Failed to create provider')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async (id) => {
    if (!confirm('Deactivate this provider? They will no longer receive quote requests.')) return
    setError(null); setErrorFallback(null)
    try {
      await api.request(`/api/v2/providers/${id}`, { method: 'DELETE' })
      setBanner({ kind: 'ok', text: 'Provider deactivated.' })
      await load()
    } catch (err) {
      setError(err); setErrorFallback('Failed to deactivate')
    }
  }

  const handleActivate = async (id) => {
    setError(null); setErrorFallback(null)
    try {
      await api.request(`/api/v2/providers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'ACTIVE' })
      })
      setBanner({ kind: 'ok', text: 'Provider activated.' })
      await load()
    } catch (err) {
      setError(err); setErrorFallback('Failed to activate')
    }
  }

  return (
    <div>
      <div className="v2-page-header">
        <div>
          <h1 className="v2-page-title">Providers</h1>
          <p className="v2-page-subtitle">
            {loading ? 'Loading…' : `${providers.length} provider${providers.length === 1 ? '' : 's'} configured`}
          </p>
        </div>
        <button className="v2-btn v2-btn--primary" onClick={() => {
          if (showForm) {
            resetForm()
          } else {
            setForm(EMPTY_FORM)
            setEditingId(null)
            setShowForm(true)
          }
        }}>
          {showForm ? 'Cancel' : '+ Add Provider'}
        </button>
      </div>

      {banner && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 6, fontSize: 13,
          background: banner.kind === 'ok' ? 'rgba(92,122,84,0.12)' : 'rgba(139,58,58,0.12)',
          color: banner.kind === 'ok' ? '#5C7A54' : '#8B3A3A',
          border: `1px solid ${banner.kind === 'ok' ? '#5C7A54' : '#8B3A3A'}`
        }}>{banner.text}</div>
      )}

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 6, fontSize: 13,
          background: 'rgba(139,58,58,0.08)', color: '#8B3A3A', border: '1px solid #8B3A3A'
        }}>{friendlyErrorMessage(error, errorFallback)}</div>
      )}

      {showForm && (
        <div className="v2-card" style={{ padding: 18, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 10 }}>
            {editingId ? 'Edit provider' : 'New provider'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: '#8F8F85' }}>Name <span style={{ color: '#8B3A3A' }}>*</span></label>
              <input className="v2-form-input" style={{ width: '100%' }}
                     value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#8F8F85' }}>Type <span style={{ color: '#8B3A3A' }}>*</span></label>
              <select className={`v2-form-input${!form.serviceType ? ' v2-form-input--placeholder' : ''}`} style={{ width: '100%' }}
                      value={form.serviceType} onChange={(e) => setForm({ ...form, serviceType: e.target.value })}>
                <option value="" disabled>Select provider type</option>
                {PROVIDER_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {isTrucking(form.serviceType) && (
              <div>
                <label style={{ fontSize: 12, color: '#8F8F85' }}>Truckload mode <span style={{ color: '#8B3A3A' }}>*</span></label>
                <select className={`v2-form-input${!form.truckingMode ? ' v2-form-input--placeholder' : ''}`} style={{ width: '100%' }}
                        value={form.truckingMode} onChange={(e) => setForm({ ...form, truckingMode: e.target.value })}>
                  <option value="" disabled>Select truckload mode</option>
                  {TRUCKING_MODE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <div style={{ fontSize: 11, color: '#8F8F85', marginTop: 4 }}>
                  Only quote requests for the modes selected here reach this carrier.
                </div>
              </div>
            )}
            <div>
              <label style={{ fontSize: 12, color: '#8F8F85' }}>Contact email <span style={{ color: '#8B3A3A' }}>*</span></label>
              <input className="v2-form-input" style={{ width: '100%' }} type="email"
                     value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#8F8F85' }}>Contact name</label>
              <input className="v2-form-input" style={{ width: '100%' }}
                     value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#8F8F85' }}>Preferred channel</label>
              <select className="v2-form-input" style={{ width: '100%' }}
                      value={form.preferredChannel} onChange={(e) => setForm({ ...form, preferredChannel: e.target.value })}>
                <option value="email">Email</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button className="v2-btn v2-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create provider'}
            </button>
            <button className="v2-btn v2-btn--outline" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="v2-providers-toolbar">
        <div className="v2-providers-filters">
          {FILTERS.map((f) => (
            <button key={f.key}
                    className={`v2-filter-chip${filter === f.key ? ' v2-filter-chip--active' : ''}`}
                    onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
        </div>
        <div className="v2-providers-search">
          <input className="v2-search-input" type="text" placeholder="Search providers…"
                 value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="v2-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="v2-table-wrapper">
          <table className="v2-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Reliability</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, opacity: 0.6 }}>
                  Loading providers…
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, opacity: 0.6 }}>
                  {providers.length === 0
                    ? 'No providers yet. Add your first one to start sending quote requests.'
                    : 'No providers match your filters.'}
                </td></tr>
              ) : (
                filtered.map((p) => {
                  const pct = reliabilityPct(p.reliability_score)
                  const color = reliabilityColor(pct)
                  return (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 500 }}>{p.name}</td>
                      <td style={{ fontSize: 13, opacity: 0.7 }}>{p.contact_email}</td>
                      <td>
                        <span className={`v2-provider-type-badge v2-provider-type-badge--${(p.service_type || 'ocean_freight').replace('_', '-')}`}>
                          {(p.service_type || 'ocean_freight') === 'domestic_trucking' ? 'Domestic Trucking' : 'Intl. Ocean Freight'}
                        </span>
                        {isTrucking(p.service_type) && (
                          <span style={{ fontSize: 11, color: '#8F8F85', marginLeft: 8 }}>
                            {TRUCKING_MODE_LABELS[p.trucking_mode] || 'mode not set'}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="v2-reliability-bar">
                          <div className="v2-reliability-track">
                            <div className="v2-reliability-fill"
                                 style={{ width: `${pct}%`, background: color }} />
                          </div>
                          <span className="v2-reliability-label" style={{ color }}>{pct}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={`v2-badge v2-status-badge--${p.status?.toLowerCase()}`}>
                          {p.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="v2-provider-actions">
                          {p.status === 'ACTIVE' ? (
                            <>
                              <button className="v2-action-btn" onClick={() => handleEdit(p)}>
                                Edit
                              </button>
                              <button className="v2-action-btn" onClick={() => handleDeactivate(p.id)}>
                                Deactivate
                              </button>
                            </>
                          ) : (
                            <button className="v2-action-btn" onClick={() => handleActivate(p.id)}>
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
